import './TeamleadWithdrawals.scss';
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import EmptyState from '../../components/Layout/EmptyState';
import Toast from '../../components/Toast/Toast';
import CopyButton from '../../components/CopyButton/CopyButton';
import { getApiErrorMessage } from '../../utils/apiErrors';

const STATUS_MAP = {
    new: { text: 'Новая', cls: 'new' },
    approved: { text: 'Одобрена', cls: 'approved' },
    completed: { text: 'Завершена', cls: 'completed' },
    failed: { text: 'Ошибка', cls: 'failed' },
    canceled: { text: 'Отменена', cls: 'canceled' },
};

export default function TeamleadWithdrawals() {
    const { t } = useTranslation('layout');
    const api_url = useSelector((state) => state.API_URL);

    const [withdrawals, setWithdrawals] = useState([]);
    const [balance, setBalance] = useState(0);
    const [minAmount, setMinAmount] = useState(50);
    const [fee, setFee] = useState(3);
    const [enabled, setEnabled] = useState(true);
    const [loading, setLoading] = useState(true);

    const [amount, setAmount] = useState('');
    const [wallet, setWallet] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null);

    const fetchData = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        if (!jwtToken) return;
        try {
            const response = await fetch(api_url + 'teamlead/withdrawals/', {
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
            });
            if (response.ok) {
                const data = await response.json();
                setWithdrawals(data.withdrawals || []);
                setBalance(data.teamlead_balance || 0);
                setMinAmount(data.min_amount ?? 50);
                setFee(data.fee ?? 3);
                setEnabled(data.enabled);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [api_url]);

    useProtectedInterval(fetchData, [api_url]);

    const handleSubmit = async () => {
        const amt = Number(amount);
        if (!Number.isFinite(amt) || amt < minAmount) {
            setToast({ type: 'error', message: `Минимальная сумма: ${minAmount} USDT` });
            return;
        }
        if (!wallet.startsWith('T') || wallet.length < 30) {
            setToast({ type: 'error', message: 'Укажите корректный TRC-20 адрес' });
            return;
        }

        setSubmitting(true);
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(api_url + 'teamlead/withdrawals/', {
                method: 'POST',
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount: amt, wallet }),
            });
            const data = await response.json().catch(() => null);
            if (response.ok) {
                setToast({ type: 'success', message: data.message });
                setAmount('');
                setWallet('');
                fetchData();
            } else {
                setToast({ type: 'error', message: getApiErrorMessage(data) });
            }
        } catch (e) {
            setToast({ type: 'error', message: 'Ошибка сети' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async (id) => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(api_url + `teamlead/withdrawals/${id}/cancel/`, {
                method: 'POST',
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
            });
            const data = await response.json().catch(() => null);
            if (response.ok) {
                setToast({ type: 'success', message: data.message });
                fetchData();
            } else {
                setToast({ type: 'error', message: getApiErrorMessage(data) });
            }
        } catch (e) {
            setToast({ type: 'error', message: 'Ошибка сети' });
        }
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return d.toLocaleDateString('ru-RU') + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    };

    const netAmount = parseFloat(amount) > 0 ? (parseFloat(amount) - fee).toFixed(2) : '0.00';

    if (loading) return <div style={{ padding: '24px' }}><h2>{t('sidebar.menu.withdrawals')}</h2><p>Загрузка...</p></div>;

    return (
        <div className="Withdrawals">
            {toast && <Toast show type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
            <h2>{t('sidebar.menu.withdrawals')}</h2>

            {/* Форма */}
            <div className="Withdrawals__form-card">
                <div className="Withdrawals__form-info">
                    <span>Баланс: <b>{balance} $</b></span>
                    <span>Мин. сумма: <b>{minAmount} $</b></span>
                    <span>Комиссия: <b>{fee} $</b></span>
                    {parseFloat(amount) > 0 && <span>К получению: <b>{netAmount} $</b></span>}
                </div>

                {!enabled ? (
                    <p className="Withdrawals__disabled">Вывод средств временно отключён</p>
                ) : (
                    <>
                        <div className="Withdrawals__form-row">
                            <input
                                className="Withdrawals__input"
                                type="number"
                                placeholder={`Сумма (мин. ${minAmount})`}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                disabled={submitting}
                                min={minAmount}
                                step="1"
                            />
                        </div>
                        <div className="Withdrawals__form-row">
                            <input
                                className="Withdrawals__input"
                                type="text"
                                placeholder="TRC-20 адрес кошелька (T...)"
                                value={wallet}
                                onChange={(e) => setWallet(e.target.value)}
                                disabled={submitting}
                            />
                            <button
                                className="Withdrawals__submit"
                                onClick={handleSubmit}
                                disabled={submitting || !amount || !wallet}
                            >
                                {submitting ? 'Отправка...' : 'Вывести'}
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* История */}
            <h3 className="Withdrawals__subtitle">История выводов</h3>

            {withdrawals.length === 0 ? (
                <EmptyState message="Нет выводов" />
            ) : (
                <div className="Withdrawals__table-wrap">
                    <table className="Withdrawals__table">
                        <thead>
                            <tr>
                                <th>Сумма</th>
                                <th>Кошелёк</th>
                                <th>Статус</th>
                                <th>Дата</th>
                                <th>TXID</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {withdrawals.map(w => {
                                const st = STATUS_MAP[w.status] || { text: w.status, cls: '' };
                                return (
                                    <tr key={w.id}>
                                        <td className="Withdrawals__amount">{w.amount} $</td>
                                        <td className="Withdrawals__wallet">
                                            {w.wallet.substring(0, 8)}...{w.wallet.substring(w.wallet.length - 4)}
                                            <CopyButton text={w.wallet} />
                                        </td>
                                        <td><span className={`Withdrawals__status Withdrawals__status--${st.cls}`}>{st.text}</span></td>
                                        <td className="Withdrawals__date">{formatDate(w.created)}</td>
                                        <td className="Withdrawals__txid">
                                            {w.txid ? (
                                                <><span>{w.txid.substring(0, 10)}...</span><CopyButton text={w.txid} /></>
                                            ) : '—'}
                                        </td>
                                        <td>
                                            {w.status === 'new' && (
                                                <button className="Withdrawals__cancel-btn" onClick={() => handleCancel(w.id)}>Отменить</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
